import { useCallback, useMemo } from "react";
import type { ChangeEvent } from "react";
import {
  Alert,
  Checkbox,
  Loader,
  NumberInput,
  Paper,
  Radio,
  Select,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { IconAlertTriangle, IconInfoCircle } from "@tabler/icons-react";
import type {
  ClientIdentityDirective,
  ClientIdentityMode,
  ClientIdentityPreview,
  RenumberBlockedReason,
} from "../../types/clientIdentity";
import {
  formatCaseNumber,
  MAX_CASE_NUMBER_SEQUENCE,
  MIN_CASE_NUMBER_SEQUENCE,
  parseCaseNumber,
  validateCaseNumberSequence,
} from "../../utils/caseNumberSequence";
import { getPrefixOverrideWarningDetails } from "../../utils/prefixOverrideWarning";

const NOT_DETERMINED_LABEL = "Not determined";

/** FR-013 — billing is shown as pending rather than asserting a scenario the case cannot support. */
const BILLING_PENDING_LABEL = "Pending — no insurance recorded";

const DEFAULT_SEQUENCE_DESCRIPTION =
  "Leave blank to use the lowest available sequence number.";

const SEQUENCE_RANGE_LABEL = "Whole numbers only (1–99,999).";

function describeSequenceAvailability(
  availability:
    | ClientIdentityPreview["activePrefixes"][number]["sequenceAvailability"]
    | undefined
): string {
  if (!availability) {
    return `${DEFAULT_SEQUENCE_DESCRIPTION} ${SEQUENCE_RANGE_LABEL}`;
  }

  const releasedSequences = availability.releasedSequences.join(", ");
  const nextSequence = availability.nextSequence;
  const availableNumbers = [
    releasedSequences,
    nextSequence ? `${nextSequence} or above` : "",
  ].filter(Boolean);

  if (availableNumbers.length === 0) {
    return `No sequence numbers are currently available. ${SEQUENCE_RANGE_LABEL}`;
  }

  const availabilityLabel = availability.hasMoreReleasedSequences
    ? `Available sequence numbers include ${availableNumbers.join(
        ", "
      )}; additional released numbers may also be available.`
    : `Available sequence numbers: ${availableNumbers.join(", ")}.`;

  return `${availabilityLabel} ${DEFAULT_SEQUENCE_DESCRIPTION} ${SEQUENCE_RANGE_LABEL}`;
}

/** FR-014 — plain-language reasons a case can never be renumbered, whatever its client resolves to. */
const RENUMBER_BLOCKED_EXPLANATIONS: Record<RenumberBlockedReason, string> = {
  pass_through:
    "This case cannot be renumbered: the number originates outside the lab, so the lab does not own the sequence.",
  additional_stain_lineage:
    "This case cannot be renumbered: the number is shared with a linked additional-stain case.",
};

interface RenumberChoice {
  value: ClientIdentityMode;
  label: string;
  description: string;
}

const KEEP_CHOICE: RenumberChoice = {
  value: "keep",
  label: "Keep the current prefix and case number",
  description:
    "The client details are still corrected. Nothing printed or spoken about this case becomes stale.",
};

/** FR-009 — exactly three outcomes, in the order the spec names them. */
const RENUMBER_CHOICES: RenumberChoice[] = [
  {
    value: "apply",
    label: "Apply the new prefix and case number",
    description:
      "Moves the case onto the resolved prefix and allocates the next available number.",
  },
  KEEP_CHOICE,
  {
    value: "manual",
    label: "Set the prefix and case number manually",
    description:
      "Choose the prefix yourself, and optionally the sequence within it.",
  },
];

export interface ClientIdentityImpactPanelProps {
  preview: ClientIdentityPreview | null;
  isLoading: boolean;
  directive: ClientIdentityDirective;
  onDirectiveChange: (directive: ClientIdentityDirective) => void;
}

/**
 * States what a candidate client identity would do to the case's prefix, case number and billing
 * scenario, and collects the operator's explicit renumbering choice.
 *
 * Presentational only — the panel never renumbers anything. It reads a preview the caller fetched
 * and reports the chosen directive upward, so no case moves without a deliberate selection
 * (FR-010).
 */
export default function ClientIdentityImpactPanel({
  preview,
  isLoading,
  directive,
  onDirectiveChange,
}: ClientIdentityImpactPanelProps) {
  // A blocked case has exactly one lawful outcome, so the panel reads as "keep" regardless of what
  // the caller is holding (FR-014).
  const selectedMode = useMemo<ClientIdentityMode>(
    () => (preview?.renumberBlocked ? "keep" : directive.mode),
    [directive.mode, preview?.renumberBlocked]
  );

  const handleModeChange = useCallback(
    (value: string) => {
      const mode = value as ClientIdentityMode;
      if (mode !== "manual") {
        onDirectiveChange({ mode });
        return;
      }
      // Seed manual mode with the resolved prefix so the operator is not warned about an override
      // they have not made yet.
      onDirectiveChange({
        mode,
        prefixCode:
          directive.prefixCode ?? preview?.proposed.prefixCode ?? null,
        sequence: directive.sequence ?? null,
        acknowledgedOverride: false,
      });
    },
    [
      directive.prefixCode,
      directive.sequence,
      onDirectiveChange,
      preview?.proposed.prefixCode,
    ]
  );

  const handleManualPrefixChange = useCallback(
    (value: string | null) => {
      // A different prefix is a different override, so a prior acknowledgement no longer applies.
      onDirectiveChange({
        ...directive,
        prefixCode: value,
        acknowledgedOverride: false,
      });
    },
    [directive, onDirectiveChange]
  );

  const handleManualSequenceChange = useCallback(
    (value: number | string) => {
      const entered = typeof value === "string" ? value.trim() : value;
      const parsed = entered === "" ? null : Number(entered);
      onDirectiveChange({
        ...directive,
        sequence: parsed !== null && Number.isNaN(parsed) ? null : parsed,
      });
    },
    [directive, onDirectiveChange]
  );

  const handleOverrideAcknowledgeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onDirectiveChange({
        ...directive,
        acknowledgedOverride: event.currentTarget.checked,
      });
    },
    [directive, onDirectiveChange]
  );

  const comparisonRows = useMemo(() => {
    if (!preview) return [];
    const { current, proposed } = preview;
    return [
      {
        key: "prefix",
        label: "Prefix",
        currentValue: current.prefixCode ?? NOT_DETERMINED_LABEL,
        proposedValue: proposed.prefixCode ?? NOT_DETERMINED_LABEL,
      },
      {
        key: "case-number",
        label: "Case number",
        currentValue: current.caseNumber,
        proposedValue: proposed.caseNumber,
      },
      {
        key: "billing",
        label: "Billing scenario",
        currentValue: current.billingScenarioName ?? NOT_DETERMINED_LABEL,
        proposedValue: proposed.billingPending
          ? BILLING_PENDING_LABEL
          : proposed.billingScenarioName ?? NOT_DETERMINED_LABEL,
      },
    ];
  }, [preview]);

  const isWarningTone = useMemo(
    () =>
      !!preview && (preview.prefixWouldChange || preview.proposed.isFallback),
    [preview]
  );

  // The backend only allocates a fresh next-available number when the prefix actually moves and
  // renumbering is possible; otherwise `proposed.caseNumber` echoes the number the case already has.
  const showsNextAvailableNumber = useMemo(
    () =>
      !!preview &&
      preview.prefixWouldChange &&
      preview.renumberBlocked === null,
    [preview]
  );

  const blockedExplanation = useMemo(
    () =>
      preview?.renumberBlocked
        ? RENUMBER_BLOCKED_EXPLANATIONS[preview.renumberBlocked]
        : null,
    [preview?.renumberBlocked]
  );

  const prefixOptions = useMemo(
    () =>
      (preview?.activePrefixes ?? []).map((prefix) => ({
        value: prefix.code,
        label: prefix.code,
        disabled: !prefix.isActive,
      })),
    [preview?.activePrefixes]
  );

  const selectedSequenceAvailability = useMemo(
    () =>
      preview?.activePrefixes.find(
        (prefix) => prefix.code === directive.prefixCode
      )?.sequenceAvailability,
    [directive.prefixCode, preview?.activePrefixes]
  );

  const sequenceDescription = useMemo(
    () => describeSequenceAvailability(selectedSequenceAvailability),
    [selectedSequenceAvailability]
  );

  const overrideWarning = useMemo(
    () =>
      getPrefixOverrideWarningDetails({
        selectedPrefix: directive.prefixCode,
        computedPrefix: preview?.proposed.prefixCode,
      }),
    [directive.prefixCode, preview?.proposed.prefixCode]
  );

  // FR-018 — a blank sequence is a valid instruction ("take the next available"), so only an
  // entered value is range-checked.
  const manualSequenceError = useMemo(() => {
    if (selectedMode !== "manual") return null;
    if (directive.sequence === null || directive.sequence === undefined) {
      return null;
    }
    return validateCaseNumberSequence(directive.sequence);
  }, [directive.sequence, selectedMode]);

  const manualSequenceValue = useMemo<number | string>(
    () =>
      typeof directive.sequence === "number" &&
      Number.isFinite(directive.sequence)
        ? directive.sequence
        : "",
    [directive.sequence]
  );

  const manualCaseNumberPreview = useMemo(() => {
    if (selectedMode !== "manual") return null;
    const chosenPrefix = directive.prefixCode?.trim().toUpperCase();
    if (!chosenPrefix) return null;
    if (directive.sequence === null || directive.sequence === undefined) {
      return `${chosenPrefix} — next available number`;
    }
    if (validateCaseNumberSequence(directive.sequence)) return null;
    const year = parseCaseNumber(preview?.current.caseNumber ?? "")?.year;
    if (!year) return null;
    return formatCaseNumber(
      { prefix: chosenPrefix, year },
      Number(directive.sequence)
    );
  }, [directive.prefixCode, directive.sequence, preview, selectedMode]);

  const panelTitle = useMemo(() => {
    if (!preview) return "";
    if (!preview.prefixWouldChange) return "Case identity is unaffected";
    return preview.renumberBlocked
      ? "The resolved prefix differs, but this case cannot be renumbered"
      : "This change would move the case to a different prefix";
  }, [preview]);

  if (isLoading) {
    return (
      <Alert
        color="gray"
        variant="light"
        role="status"
        icon={<Loader size={16} />}
        title="Checking the case identity"
      >
        Working out what these client details mean for the prefix, case number
        and billing scenario.
      </Alert>
    );
  }

  if (!preview) return null;

  return (
    <Alert
      color={isWarningTone ? "orange" : "blue"}
      variant="light"
      role="status"
      icon={
        isWarningTone ? (
          <IconAlertTriangle size={18} />
        ) : (
          <IconInfoCircle size={18} />
        )
      }
      title={panelTitle}
    >
      <Stack gap="sm">
        {!preview.prefixWouldChange && (
          <Text size="sm">
            {`These client details resolve to the prefix the case already has. Case number ${preview.current.caseNumber} and its billing scenario stay exactly as they are.`}
          </Text>
        )}

        {preview.proposed.isFallback && (
          <Text size="sm" fw={500}>
            {`No client match — this resolves to the fallback catch-all prefix ${
              preview.proposed.prefixCode ?? NOT_DETERMINED_LABEL
            }, not to a prefix mapped to this client.`}
          </Text>
        )}

        <Table
          horizontalSpacing="xs"
          verticalSpacing={4}
          withRowBorders={false}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Detail</Table.Th>
              <Table.Th>Now</Table.Th>
              <Table.Th>
                {preview.prefixWouldChange
                  ? "After this change"
                  : "Resolved from these details"}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {comparisonRows.map((row) => (
              <Table.Tr key={row.key}>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {row.label}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{row.currentValue}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={600}>
                    {row.proposedValue}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {showsNextAvailableNumber && (
          <Text size="xs" c="dimmed">
            {`${preview.proposed.caseNumber} is the next available number for ${preview.proposed.prefixCode}. It is not reserved — another case may take it before you save.`}
          </Text>
        )}

        {preview.prefixWouldChange && blockedExplanation && (
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              {blockedExplanation}
            </Text>
            <Radio.Group
              value="keep"
              onChange={handleModeChange}
              label="What should happen to the prefix and case number?"
            >
              <Stack gap="xs" mt="xs">
                <Radio
                  value={KEEP_CHOICE.value}
                  label={KEEP_CHOICE.label}
                  description={KEEP_CHOICE.description}
                />
              </Stack>
            </Radio.Group>
          </Stack>
        )}

        {preview.prefixWouldChange && !blockedExplanation && (
          <Radio.Group
            value={selectedMode}
            onChange={handleModeChange}
            label="What should happen to the prefix and case number?"
          >
            <Stack gap="xs" mt="xs">
              {RENUMBER_CHOICES.map((choice) => (
                <Radio
                  key={choice.value}
                  value={choice.value}
                  label={choice.label}
                  description={choice.description}
                />
              ))}
            </Stack>
          </Radio.Group>
        )}

        {preview.prefixWouldChange &&
          !blockedExplanation &&
          selectedMode === "manual" && (
            <Paper withBorder p="sm" radius="sm">
              <Stack gap="sm">
                <Select
                  label="Prefix"
                  description="Choose from the prefixes currently in use."
                  placeholder="Select a prefix"
                  data={prefixOptions}
                  value={directive.prefixCode ?? null}
                  onChange={handleManualPrefixChange}
                  searchable
                  nothingFoundMessage="No matching prefix"
                  comboboxProps={{
                    withinPortal: true,
                    position: "bottom-start",
                    offset: 4,
                  }}
                />
                <NumberInput
                  label="Sequence number"
                  description={sequenceDescription}
                  placeholder="Next available"
                  min={MIN_CASE_NUMBER_SEQUENCE}
                  max={MAX_CASE_NUMBER_SEQUENCE}
                  clampBehavior="none"
                  allowDecimal={false}
                  allowNegative={false}
                  value={manualSequenceValue}
                  onChange={handleManualSequenceChange}
                  error={manualSequenceError}
                />
                {manualCaseNumberPreview && (
                  <Text size="sm" c="dimmed">
                    {`Resulting case number: ${manualCaseNumberPreview}`}
                  </Text>
                )}
                {overrideWarning.shouldWarn && (
                  <Alert
                    color="orange"
                    variant="light"
                    role="alert"
                    icon={<IconAlertTriangle size={16} />}
                    title="You are overriding the computed prefix"
                  >
                    <Stack gap="xs">
                      <Text size="sm">
                        {`These client details resolve to ${overrideWarning.computedPrefix}, but you have chosen ${overrideWarning.selectedPrefix}. The case number, report header and billing scenario will follow the prefix you chose.`}
                      </Text>
                      <Checkbox
                        checked={!!directive.acknowledgedOverride}
                        onChange={handleOverrideAcknowledgeChange}
                        label={`I understand I am overriding ${overrideWarning.computedPrefix} with ${overrideWarning.selectedPrefix}`}
                      />
                    </Stack>
                  </Alert>
                )}
              </Stack>
            </Paper>
          )}
      </Stack>
    </Alert>
  );
}
